/**
 * NextDev cart drawer - the theme's cart drawer.
 *
 * Structure:
 *   1. Constants
 *   2. Drawer lookup
 *   3. Celebration effects (confetti / fly-to-cart)
 *   4. Helpers (fetch, section params, html parsing)
 *   5. <nextdev-cart-drawer> custom element
 *        render - open/close - state - countdown timer -
 *        events - quantity/remove - combined lines - upsells - protection -
 *        animations
 *   6. Header cart icon interceptor
 *   7. product-form.js adapter + public handle
 *
 * Wiring: product-form.js calls getNextDevCartAdapter() and uses the returned
 * proxy as this.cart. Load with defer in <head>, BEFORE product-form.js.
 * Section name 'nextdev-cart-drawer' is fetched via the Sections Rendering
 * API. It has to match the section's file name exactly: ask for a name the
 * theme does not have and the answer carries no section, the drawer keeps
 * whatever markup the page was loaded with, and a cart that was empty then
 * still reads empty after an add. The css classes keep their double dash,
 * nextdev--cart-drawer__inner and the rest: those are our class convention,
 * not a file name, and renaming them would only break the css.
 */
(function () {
  'use strict';

  /* ----------------------------------------------------------------
     1. Constants
     ---------------------------------------------------------------- */
  var SECTION = 'nextdev-cart-drawer';
  var ROOT_ID = 'NextDevCartDrawer';
  var TIMER_KEY = 'nextdev_cart_timer_end';
  var CHECKOUT_URL = '/checkout';

  var timerExpired = false;

  /* ----------------------------------------------------------------
     2. Drawer lookup
     ---------------------------------------------------------------- */
  function getDrawer() {
    return document.querySelector('nextdev-cart-drawer');
  }

  /* ----------------------------------------------------------------
     3. Celebration effects
     ---------------------------------------------------------------- */
  var fx = {
    layer: null,
    reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,

    mount: function () {
      if (this.layer) return;
      this.layer = document.createElement('div');
      this.layer.className = 'nextdev--cart-fx';
      document.body.appendChild(this.layer);
    },

    confetti: function (x, y) {
      if (this.reduced) return;
      this.mount();
      var colors = ['#1fa463', '#fe9a01', '#ffd166', '#7ad4a4', '#b06400', '#ffe3bb'];
      var burst = document.createElement('div');
      burst.className = 'nextdev--cart-burst';
      burst.style.left = x + 'px';
      burst.style.top = y + 'px';

      for (var i = 0; i < 14; i++) {
        var p = document.createElement('i');
        var angle = (-155 + Math.random() * 130) * Math.PI / 180;
        var dist = 28 + Math.random() * 46;
        p.style.setProperty('--dx', Math.cos(angle) * dist * 0.75 + 'px');
        p.style.setProperty('--dy', Math.sin(angle) * dist + 'px');
        p.style.setProperty('--rot', (Math.random() * 540 - 270) + 'deg');
        p.style.background = colors[i % colors.length];
        p.style.animationDelay = (Math.random() * 90) + 'ms';
        if (i % 3 === 0) p.style.borderRadius = '50%';
        burst.appendChild(p);
      }

      this.layer.appendChild(burst);
      setTimeout(function () { burst.remove(); }, 1100);
    },

    bubble: function (x, y, text, opts) {
      if (this.reduced || !text) return;
      this.mount();
      opts = opts || {};
      var ms = opts.duration || 2600;

      var anchor = document.createElement('div');
      anchor.className = 'nextdev--cart-fx-anchor' + (opts.align === 'right' ? ' nextdev--cart-fx-anchor--right' : '');
      anchor.style.left = x + 'px';
      anchor.style.top = y + 'px';

      var b = document.createElement('div');
      b.className = 'nextdev--cart-woohoo';
      b.style.animationDuration = ms + 'ms';
      b.textContent = text;

      anchor.appendChild(b);
      this.layer.appendChild(anchor);
      setTimeout(function () { anchor.remove(); }, ms + 100);
    },

    fly: function (img, target) {
      if (this.reduced || !img || !target) return;
      var r = img.getBoundingClientRect();
      var t = target.getBoundingClientRect();
      var el = img.cloneNode();
      el.className = 'nextdev--cart-fly';
      el.style.left = r.left + 'px';
      el.style.top = r.top + 'px';
      el.style.width = r.width + 'px';
      el.style.height = r.height + 'px';
      document.body.appendChild(el);

      requestAnimationFrame(function () {
        el.style.transform = 'translate(' + (t.left + 24 - r.left) + 'px,' + (t.top + 24 - r.top) + 'px) scale(.45)';
        el.style.opacity = '.2';
        el.style.borderRadius = '50%';
      });

      setTimeout(function () { el.remove(); }, 660);
    }
  };

  /* ----------------------------------------------------------------
     4. Helpers
     ---------------------------------------------------------------- */
  function centerOf(el) {
    var r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  function sectionParams() {
    return {
      sections: [SECTION, 'cart-icon-bubble'],
      sections_url: window.location.pathname
    };
  }

  function postCart(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || data.status) {
          var err = new Error(data.description || data.message || 'Cart error');
          err.payload = data;
          throw err;
        }
        return data;
      });
    });
  }

  function innerOf(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var node = doc.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  /* ----------------------------------------------------------------
     5. <nextdev-cart-drawer> custom element
     ---------------------------------------------------------------- */
  class NextDevCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.busy = false;
      this.activeElement = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keyup', function (e) {
        if (e.code === 'Escape') this.close();
      }.bind(this));

      window.NextDevCartDrawer = this;
      this.afterRender();
    }

    /* ---- render ---- */

    getSectionsToRender() {
      return [
        { id: SECTION, selector: '#' + ROOT_ID },
        { id: 'cart-icon-bubble' }
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    async renderContents(parsedState, open) {
      if (open === undefined) open = true;

      /* Self-heal: if the caller's payload is missing the drawer section
         (malformed state, third-party script, partial response), fetch it
         ourselves instead of silently leaving stale markup on screen. */
      if (!parsedState || !parsedState.sections || !parsedState.sections[SECTION]) {
        var path = window.location.pathname;
        if (path.indexOf('/pages/') !== -1) path = '/';
        var res = await fetch(path + '?sections=' + SECTION + ',cart-icon-bubble', {
          headers: { Accept: 'application/json' }
        });
        parsedState = { sections: await res.json() };
      }

      var drawerHtml = parsedState.sections[SECTION];
      var root = document.getElementById(ROOT_ID);
      if (drawerHtml && root) {
        var inner = innerOf(drawerHtml, '#' + ROOT_ID);
        if (inner !== null) root.innerHTML = inner;
      }

      var bubbleHtml = parsedState.sections['cart-icon-bubble'];
      var iconEl = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && iconEl) {
        var bubbleInner = innerOf(bubbleHtml, '.shopify-section');
        if (bubbleInner !== null) iconEl.innerHTML = bubbleInner;
      }

      if (open === true) this.clearTimer();

      this.afterRender();

      if (open === true) {
        setTimeout(this.open.bind(this), 250);
      }
    }

    /* ---- open / close ---- */

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      this.beginTimer();

      setTimeout(function () {
        this.classList.add('nextdev--cart-drawer--animating', 'nextdev--cart-drawer--active');
      }.bind(this));

      document.body.classList.add('overflow-hidden');

      this.addEventListener('transitionend', function () {
        var panel = this.querySelector('.nextdev--cart-drawer__inner');
        if (panel && typeof trapFocus === 'function') trapFocus(this, panel);
      }.bind(this), { once: true });
    }

    close() {
      this.classList.remove('nextdev--cart-drawer--active');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    }

    /* ---- state ---- */

    get panel() {
      return this.querySelector('.nextdev--cart-drawer__inner');
    }

    get visibleCount() {
      var p = this.panel;
      return p ? parseInt(p.dataset.visibleCount || '0', 10) : 0;
    }

    setBusy(on) {
      this.busy = on;
      this.classList.toggle('nextdev--cart-drawer--busy', !!on);
      this.syncDisabled();
    }

    syncDisabled() {
      var disabled = !!this.busy;
      Array.prototype.forEach.call(this.querySelectorAll('button'), function (btn) {
        btn.disabled = disabled;
      });
    }

    showError(message) {
      var box = this.querySelector('[data-nextdev-errors]');
      if (box) box.textContent = message || (window.cartStrings && window.cartStrings.error) || 'Something went wrong.';
    }

    afterRender() {
      this.classList.toggle('is-empty', this.visibleCount === 0);

      this.bindScrollShadow();
      this.startTimer();
      this.dropOrphanProtection();
      this.syncDisabled();

      document.dispatchEvent(new CustomEvent('nextdev:cart-rendered', { detail: { drawer: this } }));
    }

    bindScrollShadow() {
      var scroll = this.querySelector('[data-nextdev-scroll]');
      var topbar = this.querySelector('[data-nextdev-topbar]');
      if (!scroll || !topbar) return;
      scroll.addEventListener('scroll', function () {
        topbar.classList.toggle('is-stuck', scroll.scrollTop > 2);
      }, { passive: true });
    }

    get timerMinutes() {
      return parseInt(this.dataset.urgencyMinutes || '4', 10);
    }

    /* ---- countdown timer ---- */

    readDeadline() {
      try {
        return parseInt(sessionStorage.getItem(TIMER_KEY) || '0', 10) || 0;
      } catch (e) {
        return this._deadline || 0; 
      }
    }

    writeDeadline(end) {
      this._deadline = end;
      try {
        if (end) sessionStorage.setItem(TIMER_KEY, String(end));
        else sessionStorage.removeItem(TIMER_KEY);
      } catch (e) {  }
    }

    beginTimer() {
      var end = this.readDeadline();
      if (!end || end <= Date.now()) {
        this.writeDeadline(Date.now() + this.timerMinutes * 60 * 1000);
      }
      this.startTimer();
    }

    restartTimer() {
      clearInterval(this._timer);
      this.writeDeadline(Date.now() + this.timerMinutes * 60 * 1000);
    }

    clearTimer() {
      clearInterval(this._timer);
      this.writeDeadline(0);
    }

    startTimer() {
      var node = this.querySelector('[data-nextdev-timer]');
      if (!node) return;

      clearInterval(this._timer);

      var full = String(this.timerMinutes).padStart(2, '0') + ':00';
      var end = this.readDeadline();

      if (!end) {
        node.textContent = full; 
        return;
      }

      if (end <= Date.now()) {
        this.writeDeadline(0);
        node.textContent = full;
        return;
      }

      var tick = function () {
        var el = this.querySelector('[data-nextdev-timer]');
        if (!el) { clearInterval(this._timer); return; }
        var left = Math.max(0, Math.floor((end - Date.now()) / 1000));
        var m = String(Math.floor(left / 60)).padStart(2, '0');
        var s = String(left % 60).padStart(2, '0');
        el.textContent = m + ':' + s;
        if (left <= 0) {
          clearInterval(this._timer);
          this.onTimerExpired();
        }
      }.bind(this);

      tick();
      this._timer = setInterval(tick, 1000);
    }

    onTimerExpired() {
      if (timerExpired) return;
      if (this.visibleCount === 0) return;
      if (!this.classList.contains('nextdev--cart-drawer--active')) return;
      timerExpired = true;
      window.location.href = CHECKOUT_URL;
    }

    dropOrphanProtection() {
      var p = this.panel;
      if (!p || this._droppingProtection) return;
      var key = p.dataset.protectionKey;
      if (!key || this.visibleCount > 0) return;

      this._droppingProtection = true;
      postCart(window.routes.cart_change_url, Object.assign({ id: key, quantity: 0 }, sectionParams()))
        .then(function (state) { return this.renderContents(state, false); }.bind(this))
        .catch(function () {})
        .finally(function () { this._droppingProtection = false; }.bind(this));
    }

    /* ---- events ---- */

    onClick(event) {
      var target = event.target;

      if (target.closest('[data-nextdev-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      var qtyBtn = target.closest('[data-nextdev-qty]');
      if (qtyBtn) {
        event.preventDefault();
        this.changeQuantity(qtyBtn.closest('[data-nextdev-item]'), parseInt(qtyBtn.dataset.nextdevQty, 10));
        return;
      }

      if (target.closest('[data-nextdev-remove]')) {
        event.preventDefault();
        this.removeItem(target.closest('[data-nextdev-item]'));
        return;
      }

      if (target.closest('[data-nextdev-add]')) {
        event.preventDefault();
        this.addUpsell(target.closest('[data-nextdev-upsell]'));
      }
    }

    onChange(event) {
      var toggle = event.target.closest('[data-nextdev-protect-toggle]');
      if (toggle) this.toggleProtection(toggle);
    }

    /* ---- quantity / remove ---- */

    changeQuantity(row, delta) {
      if (!row || this.busy) return;
      var current = parseInt(row.dataset.quantity || '1', 10);
      var next = current + delta;
      if (next < 1) return this.removeItem(row);

      var updates = {};
      updates[row.dataset.key] = next;

      // what came with it moves with it, the way a native bundle's components
      // do: two beds, two blankets
      this.combinedKeys(row).forEach(function (key) { updates[key] = next; });

      this.setBusy(true);
      row.classList.add('is-loading');

      postCart(window.routes.cart_update_url, Object.assign({ updates: updates }, sectionParams()))
        .then(function (state) { return this.renderContents(state, false); }.bind(this))
        .catch(function (e) {
          /* Re-render from the server so the row spinner can never get stuck,
             then surface the error. */
          return this.renderContents(null, false).then(function () {
            this.showError(e.message);
          }.bind(this));
        }.bind(this))
        .finally(function () {
          if (row.isConnected) row.classList.remove('is-loading');
          this.setBusy(false);
        }.bind(this));
    }

    rowWithGifts(row) {
      var nodes = [row];
      var next = row.nextElementSibling;
      while (next && next.classList.contains('nextdev--cart-item--gift')) {
        nodes.push(next);
        next = next.nextElementSibling;
      }
      return nodes;
    }

    /* The cart keys of the lines combined with this one. A native bundle's
       components are inside the parent's own line, so the cart moves them by
       itself and their data-key is not a line to post; a line we combined is a
       line of its own, marked data-nextdev-combined, and has to be named. */
    combinedKeys(row) {
      return this.rowWithGifts(row)
        .filter(function (el) {
          return el !== row && el.hasAttribute('data-nextdev-combined') && el.dataset.key;
        })
        .map(function (el) { return el.dataset.key; });
    }

    collapseAll(nodes, done, gap) {
      var pending = nodes.length;
      if (!pending) { done(); return; }
      var finish = function () { if (--pending === 0) done(); };
      nodes.forEach(function (el) { this.collapse(el, finish, gap); }, this);
    }

    removeItem(row) {
      if (!row || this.busy) return;

      var updates = {};
      updates[row.dataset.key] = 0;

      // and what came with it goes with it, in the same post, so the cart is
      // never left holding a gift for a bed that is no longer there
      this.combinedKeys(row).forEach(function (key) { updates[key] = 0; });

      var upsellsBefore = this.upsellIds();

      this.setBusy(true);

      var request = postCart(
        window.routes.cart_update_url,
        Object.assign({ updates: updates }, sectionParams())
      );

      var settle = new Promise(function (resolve) {
        this.collapseAll(this.rowWithGifts(row), resolve);
      }.bind(this));

      Promise.all([request, settle])
        .then(function (results) { return this.renderContents(results[0], false); }.bind(this))
        .then(function () { this.animateNewUpsells(upsellsBefore); }.bind(this))
        .catch(function (e) {
          return this.renderContents(null, false).then(function () {
            this.showError(e.message);
          }.bind(this));
        }.bind(this))
        .finally(function () { this.setBusy(false); }.bind(this));
    }

    rowForVariant(variantId) {
      if (!variantId) return null;
      var rows = this.querySelectorAll('[data-nextdev-item]');
      for (var i = 0; i < rows.length; i++) {
        var key = rows[i].dataset.key || '';
        if (key === variantId || key.indexOf(variantId + ':') === 0) return rows[i];
      }
      return null;
    }

    /* ---- upsells ---- */

    upsellIds() {
      return Array.prototype.map.call(
        this.querySelectorAll('[data-nextdev-upsell]'),
        function (card) { return card.dataset.variantId; }
      );
    }

    animateNewUpsells(before) {
      Array.prototype.forEach.call(this.querySelectorAll('[data-nextdev-upsell]'), function (card) {
        if (before.indexOf(card.dataset.variantId) === -1) {
          card.classList.add('nextdev--cart-enter');
        }
      });
    }

    addUpsell(card) {
      if (!card || this.busy) return;
      this.setBusy(true);

      var btn = card.querySelector('[data-nextdev-add]');
      if (btn) btn.disabled = true;

      var thumb = card.querySelector('.nextdev--cart-up__thumb');
      var items = this.querySelector('[data-nextdev-items]');
      if (thumb) {
        var c = centerOf(thumb);
        fx.confetti(c.x, c.y);
      }
      fx.fly(card.querySelector('img'), items);

      var saved = card.dataset.save;
      var variantId = card.dataset.variantId;

      var settle = new Promise(function (resolve) {
        setTimeout(function () {
          this.collapse(card, resolve, 12);
        }.bind(this), fx.reduced ? 0 : 340);
      }.bind(this));

      var request = postCart(
        window.routes.cart_add_url,
        Object.assign({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }, sectionParams())
      );

      Promise.all([request, settle])
        .then(function (results) {
          if (typeof window.checkCartItems === 'function') window.checkCartItems();
          this.restartTimer();
          return this.renderContents(results[0], false);
        }.bind(this))
        .then(function () {
          var row = this.rowForVariant(variantId);
          if (!row) return;
          row.classList.add('nextdev--cart-enter', 'nextdev--cart-flash');
          row.scrollIntoView({ block: 'nearest', behavior: fx.reduced ? 'auto' : 'smooth' });
          var r = row.getBoundingClientRect();
          if (saved && saved !== '0') {
            fx.bubble(r.left + r.width / 2, r.top + 6, 'Score! You saved ' + saved + ' 🎉');
          }
        }.bind(this))
        .catch(function (e) {
          this.showError(e.message);
          if (btn) btn.disabled = false;
        }.bind(this))
        .finally(function () { this.setBusy(false); }.bind(this));
    }

    /* ---- shipping protection ---- */

    toggleProtection(input) {
      if (this.busy) {
        input.checked = !input.checked;
        return;
      }
      this.setBusy(true);

      var on = input.checked;
      var row = input.closest('[data-nextdev-protect]');
      var request;

      if (on) {
        if (row) {
          var switchEl = input.closest('.nextdev--cart-switch');
          var sw = centerOf(switchEl);
          var rect = row.getBoundingClientRect();
          fx.confetti(sw.x, sw.y);
          fx.bubble(rect.right - 8, rect.top + 2, 'Woohoo! Order protected 🎉', { align: 'right', duration: 1500 });

          row.classList.remove('nextdev--cart-pop');
          switchEl.classList.remove('nextdev--cart-pop');
          void row.offsetWidth;
          row.classList.add('is-on', 'nextdev--cart-pop');
          switchEl.classList.add('nextdev--cart-pop');
        }
        request = postCart(
          window.routes.cart_add_url,
          Object.assign({ items: [{ id: parseInt(input.dataset.variantId, 10), quantity: 1 }] }, sectionParams())
        );
      } else {
        if (row) row.classList.remove('is-on');
        request = postCart(
          window.routes.cart_change_url,
          Object.assign({ id: input.dataset.key, quantity: 0 }, sectionParams())
        );
      }

      request
        .then(function (state) { return this.renderContents(state, false); }.bind(this))
        .catch(function (e) {
          input.checked = !on;
          this.showError(e.message);
        }.bind(this))
        .finally(function () { this.setBusy(false); }.bind(this));
    }

    /* ---- animations ---- */

    collapse(el, done, gap) {
      if (fx.reduced) { done(); return; }
      el.style.height = el.offsetHeight + 'px';
      el.style.overflow = 'hidden';
      requestAnimationFrame(function () {
        el.style.transition =
          'height .3s ease, opacity .25s ease, margin .3s ease, padding .3s ease, transform .3s ease';
        el.style.height = '0px';
        el.style.opacity = '0';
        el.style.paddingTop = '0';
        el.style.paddingBottom = '0';
        if (gap) el.style.marginBottom = -gap + 'px';
        el.style.transform = 'scale(.96)';
      });
      setTimeout(done, 310);
    }
  }

  if (!customElements.get('nextdev-cart-drawer')) {
    customElements.define('nextdev-cart-drawer', NextDevCartDrawer);
  }

  /* ----------------------------------------------------------------
     6. Header cart icon - capture-phase interceptor on #cart-icon-bubble
     ---------------------------------------------------------------- */
  function interceptCartIcon(event) {
    if (event.type === 'keydown') {
      var code = (event.code || '').toUpperCase();
      if (code !== 'SPACE' && code !== 'ENTER') return;
    }

    var node = event.target;
    var icon = node && node.closest ? node.closest('#cart-icon-bubble') : null;
    if (!icon) return;

    var drawer = getDrawer();
    if (!drawer || typeof drawer.open !== 'function') return;

    icon.setAttribute('role', 'button');
    icon.setAttribute('aria-haspopup', 'dialog');

    event.preventDefault();
    event.stopImmediatePropagation();
    drawer.open(icon);
  }

  function bindCartIcon() {
    document.addEventListener('click', interceptCartIcon, true);
    document.addEventListener('keydown', interceptCartIcon, true);
  }

  /* ----------------------------------------------------------------
     7. product-form.js adapter (proxy resolves to the live element)
     ---------------------------------------------------------------- */
  var adapter = null;

  function buildAdapter() {
    if (adapter) return adapter;
    if (typeof Proxy !== 'function') return null;

    adapter = new Proxy(
      {},
      {
        get: function (_target, prop) {
          var live = getDrawer();
          if (!live) return undefined;
          var value = live[prop];
          return typeof value === 'function' ? value.bind(live) : value;
        },
        set: function (_target, prop, value) {
          var live = getDrawer();
          if (live) live[prop] = value;
          return true;
        },
        has: function (_target, prop) {
          var live = getDrawer();
          return live ? prop in live : false;
        }
      }
    );

    return adapter;
  }

  window.getNextDevCartAdapter = function () {
    if (!getDrawer()) return null;
    return buildAdapter();
  };

  window.NextDevCart = {
    drawer: getDrawer
  };

  bindCartIcon();
})();